import { useEffect, useMemo, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CarouselHero from '@/components/base/CarouselHero';
import { useCollection } from '@/hooks/useCollection';
import { CHURCH_PREACHING_PHOTOS } from '@/lib/churchPhotos';
import type { Sermon } from '@/lib/types';

function getEmbedUrl(url?: string): string | null {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function SermonsPage() {
  const { rows: sermons, loading, error, reload } = useCollection<Sermon>('sermons', 'preached_on', false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [selected, setSelected] = useState<Sermon | null>(null);

  useEffect(() => {
    document.title = 'Sermons & Media — TGMCI';
    window.scrollTo(0, 0);
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>();
    sermons.forEach((s) => { if (s.category) set.add(s.category); });
    return ['All', ...Array.from(set)];
  }, [sermons]);

  const featured = sermons.find((s) => s.is_featured) ?? sermons[0];
  const rest = sermons.filter((s) => s.id !== featured?.id);

  const filtered = rest.filter((s) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      s.title?.toLowerCase().includes(q) ||
      s.speaker?.toLowerCase().includes(q) ||
      s.series?.toLowerCase().includes(q) ||
      s.scripture?.toLowerCase().includes(q);
    const matchesCategory = category === 'All' || s.category === category;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <CarouselHero
          title="Messages to Grow Your Faith"
          subtitle="Watch and listen to sermons from our ministers — teaching that transforms lives through the Word of God."
          images={CHURCH_PREACHING_PHOTOS}
          breadcrumb="Sermons"
        />

        {loading ? (
          <div className="py-20 text-center text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
            <p className="mt-2 text-sm">Loading sermons...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <p className="text-red-600 text-sm mb-3">Could not load sermons.</p>
            <button type="button" onClick={reload} className="btn-primary">Retry</button>
          </div>
        ) : (
          <>
            {/* Featured Sermon */}
            {featured && (
              <section className="py-16 bg-background-50">
                <div className="max-w-[1000px] mx-auto px-4 md:px-8">
                  <h2 className="font-heading text-xl font-semibold text-foreground-700 mb-6 flex items-center gap-2">
                    <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                    Featured Message
                  </h2>
                  <article className="bg-background-100 rounded-2xl overflow-hidden border border-background-200">
                    <div className="aspect-video bg-foreground-950">
                      {getEmbedUrl(featured.video_url) ? (
                        <iframe
                          src={getEmbedUrl(featured.video_url)!}
                          title={featured.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : featured.image ? (
                        <img src={featured.image} alt={featured.title} className="w-full h-full object-cover object-top" />
                      ) : null}
                    </div>
                    <div className="p-6 md:p-8">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {featured.series && (
                          <span className="text-xs font-label font-semibold bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full">
                            {featured.series}
                          </span>
                        )}
                        {featured.category && (
                          <span className="text-xs font-label font-semibold bg-accent-100 text-accent-800 px-2.5 py-1 rounded-full">
                            {featured.category}
                          </span>
                        )}
                        <span className="text-xs text-foreground-400 ml-auto">{formatDate(featured.preached_on)}</span>
                      </div>
                      <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                        {featured.title}
                      </h3>
                      <div className="text-foreground-500 text-sm mb-4">
                        {featured.speaker && <span className="mr-3"><i className="ri-user-line mr-1" aria-hidden="true" />{featured.speaker}</span>}
                        {featured.scripture && <span><i className="ri-book-open-line mr-1" aria-hidden="true" />{featured.scripture}</span>}
                      </div>
                      {featured.description && (
                        <p className="text-foreground-600 text-base leading-relaxed line-clamp-3">{featured.description}</p>
                      )}
                    </div>
                  </article>
                </div>
              </section>
            )}

            {/* Sermon Library */}
            <section className="py-12 pb-24 bg-background-50">
              <div className="max-w-[1200px] mx-auto px-4 md:px-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                  <h2 className="font-heading text-xl font-semibold text-foreground-700 flex items-center gap-2">
                    <i className="ri-mic-line text-primary-600" aria-hidden="true" />
                    Sermon Library
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search sermons..."
                      className="px-4 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 w-full sm:w-64"
                      aria-label="Search sermons"
                    />
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="px-4 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                      aria-label="Filter by category"
                    >
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {filtered.length === 0 ? (
                  <p className="text-center text-foreground-400 py-16">No sermons found.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((s) => {
                      const embed = getEmbedUrl(s.video_url);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setSelected(s)}
                          className="group text-left bg-background-100 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 hover:-translate-y-1 transition-all cursor-pointer"
                        >
                          <div className="h-44 overflow-hidden relative">
                            {s.image ? (
                              <img src={s.image} alt={s.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                            ) : (
                              <div className="w-full h-full bg-primary-100 flex items-center justify-center">
                                <i className="ri-mic-line text-4xl text-primary-400" aria-hidden="true" />
                              </div>
                            )}
                            {embed && (
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-700">
                                  <i className="ri-play-fill text-xl" aria-hidden="true" />
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {s.category && (
                                <span className="text-xs font-label font-semibold bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full">
                                  {s.category}
                                </span>
                              )}
                              <span className="text-xs text-foreground-400 ml-auto">{formatDate(s.preached_on)}</span>
                            </div>
                            <h3 className="font-heading font-semibold text-base text-foreground-800 leading-tight line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {s.title}
                            </h3>
                            <p className="text-xs text-foreground-500 mt-2">
                              {s.speaker && <span className="mr-2">{s.speaker}</span>}
                              {s.scripture && <span>{s.scripture}</span>}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {/* CTA */}
        <section className="py-16 bg-primary-700 text-center px-4">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Never Miss a Message</h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">
            Subscribe to our newsletter to receive the latest sermons and service updates.
          </p>
          <a href="/contact" className="btn-accent">
            <i className="ri-mail-line" aria-hidden="true" />
            Subscribe
          </a>
        </section>
      </main>

      {/* Sermon Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelected(null)} aria-hidden="true" />
          <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-foreground-700 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>

            <div className="aspect-video bg-foreground-950">
              {getEmbedUrl(selected.video_url) ? (
                <iframe
                  src={getEmbedUrl(selected.video_url)!}
                  title={selected.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : selected.image ? (
                <img src={selected.image} alt={selected.title} className="w-full h-full object-cover object-top" />
              ) : null}
            </div>

            <div className="p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {selected.series && (
                  <span className="text-xs font-label font-semibold bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full">
                    {selected.series}
                  </span>
                )}
                {selected.category && (
                  <span className="text-xs font-label font-semibold bg-accent-100 text-accent-800 px-2.5 py-1 rounded-full">
                    {selected.category}
                  </span>
                )}
                <span className="text-xs text-foreground-400 ml-auto">{formatDate(selected.preached_on)}</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-3">{selected.title}</h2>
              <div className="text-foreground-500 text-sm mb-5">
                {selected.speaker && <span className="mr-3"><i className="ri-user-line mr-1" aria-hidden="true" />{selected.speaker}</span>}
                {selected.scripture && <span><i className="ri-book-open-line mr-1" aria-hidden="true" />{selected.scripture}</span>}
              </div>
              {selected.description && (
                <p className="text-foreground-600 text-base leading-relaxed whitespace-pre-wrap">{selected.description}</p>
              )}
              {selected.audio_url && (
                <a href={selected.audio_url} target="_blank" rel="noreferrer" className="btn-outline mt-6">
                  <i className="ri-headphone-line" aria-hidden="true" />
                  Listen to Audio
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}